(() => {
  const soldOutText = 'Sold Out';
  const blockedVariants = new Set();

  const mapSelector = 'script[data-forced-oos-variants]';
  const registeredMaps = new WeakMap();

  const registerVariantMap = (root = document) => {
    let changed = false;
    const scripts = root.matches?.(mapSelector) ? [root] : root.querySelectorAll(mapSelector);
    scripts.forEach((script) => {
      const source = script.textContent || '{}';
      if (registeredMaps.get(script) === source) return;
      try {
        const map = JSON.parse(source);
        Object.keys(map).forEach((id) => {
          if (!map[id] || blockedVariants.has(String(id))) return;
          blockedVariants.add(String(id));
          changed = true;
        });
        registeredMaps.set(script, source);
      } catch (error) {
        console.warn('Unable to read forced OOS variant data.', error);
      }
    });
    return changed;
  };

  const isBlockedVariant = (variantId) => blockedVariants.has(String(variantId));

  const variantIdsFromBody = (body) => {
    if (!body) return [];

    if (body instanceof FormData || body instanceof URLSearchParams) {
      const ids = [];
      body.forEach((value, key) => {
        if (key === 'id' || key.endsWith('[id]')) ids.push(String(value));
      });
      return ids;
    }

    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        if (json.id) return [String(json.id)];
        if (Array.isArray(json.items)) return json.items.map((item) => String(item.id)).filter(Boolean);
      } catch (_) {
        return variantIdsFromBody(new URLSearchParams(body));
      }
    }

    return [];
  };

  const positiveBlockedUpdateIdsFromBody = (body) => {
    if (!body) return [];

    const blockedIds = [];
    const collectBlockedUpdate = (id, quantity) => {
      if (Number(quantity) > 0 && isBlockedVariant(id)) blockedIds.push(String(id));
    };

    if (body instanceof FormData || body instanceof URLSearchParams) {
      body.forEach((value, key) => {
        const match = String(key).match(/^updates\[(\d+)\]$/);
        if (match) collectBlockedUpdate(match[1], value);
      });
      return blockedIds;
    }

    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        if (json.updates && typeof json.updates === 'object' && !Array.isArray(json.updates)) {
          Object.entries(json.updates).forEach(([id, quantity]) => collectBlockedUpdate(id, quantity));
        }
      } catch (_) {
        return positiveBlockedUpdateIdsFromBody(new URLSearchParams(body));
      }
    }

    return blockedIds;
  };

  const isCartAddUrl = (url) => {
    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
    return String(url || '').includes('/cart/add') || String(url || '').includes(cartAddUrl);
  };

  const isCartUpdateUrl = (url) => {
    const cartUpdateUrl = window.routes?.cart_update_url || '/cart/update';
    return String(url || '').includes('/cart/update') || String(url || '').includes(cartUpdateUrl);
  };

  const blockAttempt = (formOrButton) => {
    const button = formOrButton?.querySelector?.('[name="add"], [type="submit"]') || formOrButton;
    if (!button) return;

    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');

    const text = button.querySelector('span') || button;
    text.textContent = soldOutText;
  };

  const refreshVariantPickers = (pickers = document.querySelectorAll('variant-selects')) => {
    pickers.forEach((variantPicker) => {
      if (typeof variantPicker.updateOptions !== 'function') return;

      variantPicker.updateOptions();
      variantPicker.updateMasterId();
      variantPicker.updateVariantStatuses();
      variantPicker.updateSelectedVariantStatus();
      variantPicker.updateForcedOutOfStockState();
    });
  };

  registerVariantMap();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const isCartAddForm = isCartAddUrl(form.action);
      const isCartUpdateForm = isCartUpdateUrl(form.action);
      if (!isCartAddForm && !isCartUpdateForm) return;

      if (isCartAddForm) {
        const ids = variantIdsFromBody(new FormData(form));
        if (ids.some(isBlockedVariant)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          blockAttempt(form);
        }
      }

      if (isCartUpdateForm) {
        const blockedUpdateIds = positiveBlockedUpdateIdsFromBody(new FormData(form));
        if (blockedUpdateIds.length > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          blockAttempt(form);
        }
      }
    },
    true
  );

  const originalFetch = window.fetch;
  window.fetch = function patchedFetch(input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url;

    if (isCartAddUrl(url)) {
      const ids = variantIdsFromBody(init.body);
      if (ids.some(isBlockedVariant)) {
        return Promise.resolve(
          new Response(
            JSON.stringify({
              status: 422,
              message: soldOutText,
              description: soldOutText,
              errors: soldOutText,
            }),
            { status: 422, headers: { 'Content-Type': 'application/json' } }
          )
        );
      }
    }

    if (isCartUpdateUrl(url)) {
      const blockedUpdateIds = positiveBlockedUpdateIdsFromBody(init.body);
      if (blockedUpdateIds.length > 0) {
        return Promise.resolve(
          new Response(
            JSON.stringify({
              status: 422,
              message: soldOutText,
              description: soldOutText,
              errors: soldOutText,
            }),
            { status: 422, headers: { 'Content-Type': 'application/json' } }
          )
        );
      }
    }

    return originalFetch.apply(this, arguments);
  };

  window.ForcedOutOfStock = {
    isBlockedVariant,
    soldOutText,
    registerVariantMap,
  };

  // Product data registers itself when a section or quick-add is inserted.
  // Observe only that inert JSON, never unrelated widget/page mutations.
  let refreshQueued = false;
  const registerProductData = (element) => {
    if (!registerVariantMap(element) || refreshQueued) return;
    refreshQueued = true;
    queueMicrotask(() => {
      refreshQueued = false;
      refreshVariantPickers();
    });
  };

  if (!customElements.get('forced-oos-variant-data')) {
    customElements.define(
      'forced-oos-variant-data',
      class extends HTMLElement {
        connectedCallback() {
          registerProductData(this);
          this.dataObserver ||= new MutationObserver(() => registerProductData(this));
          this.dataObserver.observe(this, { childList: true, subtree: true, characterData: true });
        }

        disconnectedCallback() {
          this.dataObserver?.disconnect();
        }
      }
    );
  }

  refreshVariantPickers();
})();

